import { useMemo, useState } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Partner, PartnerReferral } from "@modules/partners/types";
import {
  useApproveReferralMutation,
  useRejectReferralMutation,
} from "@modules/partners/services/mutations";
import { useReferralSummaryQuery } from "@modules/partners/services/queries";

export function useReferralsTab() {
  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();
  const [rejectTarget, setRejectTarget] = useState<PartnerReferral | null>(null);

  const { tab, ...filters } = getQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<PartnerReferral>({
    resource: "v1/partner_referral_admin",
    filters,
    sorters: { ordering: "-id" },
  });

  const partnerIds = useMemo(
    () =>
      [
        ...new Set(
          rows?.map((row) => row.partner).filter((id): id is number => Boolean(id))
        ),
      ],
    [rows]
  );

  const { data: partners } = useFetchResourcesByIdsQuery<ApiResponse<Partner>>({
    resource: "v1/partner_admin",
    ids: partnerIds,
  });

  const { data: summary } = useReferralSummaryQuery();

  const approveMutation = useApproveReferralMutation();
  const rejectMutation = useRejectReferralMutation();

  const handleApprove = async (id: number) => {
    await approveMutation.mutateAsync(id);
    refetch();
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    await rejectMutation.mutateAsync({
      id: rejectTarget.id,
      rejection_reason: reason,
    });
    setRejectTarget(null);
    refetch();
  };

  return {
    rows,
    loading,
    tableProps,
    summary,
    partners: partners?.results,
    rejectTarget,
    isRejecting: rejectMutation.isPending,
    setRejectTarget,
    handleApprove,
    handleReject,
    refetch,
    translate,
  };
}
